import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { getAllCompanies, getCompanyId } from '../services/company/company';
import { Company } from '../interfaces/company.interface';
import { useAuth } from './AuthContext';
import { UserRole } from '../enums/user';

interface CompanyContextData {
    companies: Company[];
    currentCompany: Company | null;
    loading: boolean;
    refreshCurrentCompany: () => Promise<Company | null>;
}

const CompanyContext = createContext<CompanyContextData>({
    companies: [],
    currentCompany: null,
    loading: true,
    refreshCurrentCompany: async () => null,
});

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user, updateCompanyVisited } = useAuth();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
    const [loading, setLoading] = useState(true);

    const resolveCompanyId = () => {
        return user?.company_visited?.id ?? null;
    };

    const refreshCurrentCompany = async () => {
        const companyId = resolveCompanyId();
        if (!user?.user?.id || !companyId) {
            setCurrentCompany(null);
            setCompanies([]);
            return null;
        }

        try {
            const role = Number(user?.user?.role);

            if (role === UserRole.sAdmin) {
                const allCompaniesResponse = await getAllCompanies();
                const allCompanies = Array.isArray(allCompaniesResponse)
                    ? allCompaniesResponse
                    : allCompaniesResponse?.data ?? [];

                setCompanies(allCompanies);

                const freshCompany =
                    allCompanies.find((company: Company) => company.id === companyId) ?? null;

                if (freshCompany) {
                    setCurrentCompany(freshCompany);
                    updateCompanyVisited(freshCompany);
                    return freshCompany;
                }

                return null;
            }

            const companyResponse = await getCompanyId(companyId);
            const freshCompany =
                companyResponse?.Item ??
                companyResponse?.company?.Item ??
                companyResponse?.company ??
                companyResponse ??
                null;

            if (freshCompany) {
                setCurrentCompany(freshCompany);
                setCompanies([freshCompany]);
                updateCompanyVisited(freshCompany);
                return freshCompany;
            }

            setCurrentCompany(null);
            setCompanies([]);
            return null;
        } catch (error) {
            console.error('Failed to refresh current company:', error);
            setCurrentCompany(null);
            return null;
        }
    };

    useEffect(() => {
        const fetchCompanies = async () => {
            setLoading(true);
            try {
                await refreshCurrentCompany();
            } catch (error) {
                console.error("Failed to fetch companies:", error);
                setCompanies([]);
                setCurrentCompany(null);
            } finally {
                setLoading(false);
            }
        };

        if (user?.user?.id) {
            void fetchCompanies();
        } else {
            setLoading(false);
            setCompanies([]);
            setCurrentCompany(null);
        }
    }, [user?.user?.id, user?.company_visited?.id, user?.user?.role]);

    return (
        <CompanyContext.Provider value={{ companies, currentCompany, loading, refreshCurrentCompany }}>
            {children}
        </CompanyContext.Provider>
    );
};

export const useCompany = () => {
    return useContext(CompanyContext);
};
